/* eslint-disable no-param-reassign */
import type { StandardChartLayerSpec } from '../../../../core/layer';
import type { Spec as TreeLayerSpec } from '../../../../layer/preset/tree/tree';
import * as utils from '../../../../util';
import { BaseSpec, ILayerTypeSpec, createLayerProcessor } from '../baseProcessor';
import type { Data } from '../../specProcessor';

export type Spec = BaseSpec;

type TreeLayerType = StandardChartLayerSpec & TreeLayerSpec & ILayerTypeSpec;

/**
 * 递归验证节点结构是否有效
 * @param node 节点对象
 * @returns 节点结构是否有效
 */
function validateNode(node: Record<string, unknown>): boolean {
  // 检查节点是否有名称
  if (!node.name) {
    utils.logger.warn('warning', 'Each node in tree data must have a name property');
    return false;
  }

  // 如果有子节点，递归验证
  if (node.children) {
    if (!Array.isArray(node.children)) {
      utils.logger.warn('warning', 'The children property must be an array');
      return false;
    }

    // 递归验证每个子节点
    return node.children.every(child => validateNode(child as Record<string, unknown>));
  }

  return true;
}

/**
 * 验证树图数据结构是否符合要求
 */
const validateTreeData = (layer: TreeLayerType, data: Data): boolean => {
  const dataIndex = layer.dataIndex || 0;
  const treeData = data[dataIndex]?.values;

  // 检查是否存在数据
  if (!treeData) {
    utils.logger.warn('warning', `No data found for "${layer.type}" layer at index ${dataIndex}`);
    return false;
  }

  // 检查是否为对象类型
  if (typeof treeData !== 'object') {
    utils.logger.warn(
      'warning',
      `The data for "${layer.type}" layer must be an object with hierarchical structure`
    );
    return false;
  }

  // 验证根节点
  if (!validateNode(treeData as unknown as Record<string, unknown>)) {
    utils.logger.warn('warning', `Invalid root node in "${layer.type}" layer data`);
    return false;
  }

  return true;
};

// 创建树图编码验证器 - 树图没有常规编码字段，但仍需要验证函数
const validateTreeEncoding = () => true;

// 创建并导出树图处理器
export const tree = createLayerProcessor<TreeLayerType>(
  'tree',
  validateTreeEncoding,
  validateTreeData
);
